import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { setAuthFlowActive } from "../../providers/D3AuthProvider";
import Alert from "../Alert";
import { Loading } from "../App";
import { Container } from "./styles";

/**
 * Auth Callback Handler for Firebase (via Deltatre Broker)
 *
 * This component handles the callback from Deltatre's authentication broker after
 * Firebase authentication. It processes the Firebase ID token/code and sends it
 * to the backend for validation via Firebase Admin SDK.
 *
 * ERROR HANDLING:
 * - Sets auth flow flag to prevent D3AuthProvider's fetch interceptor from
 *   redirecting on 401/403 errors during the auth callback
 * - This allows error messages to be displayed to the user
 * - Flag is cleared on successful auth or when component unmounts
 */

// Module-level map to track submission results across StrictMode remounts
// Maps codeKey to {error: string | null, processing: boolean}
const submissionResults = new Map<
  string,
  { error: string | null; processing: boolean }
>();

// Module-level set — survives React StrictMode's simulated unmount/remount.
// Refs reset on remount; this does not. We track each (code, state) pair so
// the second StrictMode effect invocation skips submission of an already
// in-flight single-use OIDC code.
const submittedCodes = new Set<string>();

/**
 * Get CSRF token from Django's csrftoken cookie.
 * Django automatically sets this cookie on first page load.
 */
const getCsrfToken = (): string | null => {
  const name = "csrftoken";
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() || null;
  }
  return null;
};

export const AuthCallback = () => {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);
  // Use ref to track if we're redirecting - refs update synchronously unlike state
  const isRedirecting = useRef(false);

  useEffect(() => {
    // Mark that we're in auth flow to prevent fetch interceptor from redirecting
    setAuthFlowActive(true);

    const processCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const urlHash = new URLSearchParams(window.location.hash.substring(1));

        // Check if this is a D3 OIDC callback (has 'code' in hash)
        const oidcCode = urlHash.get("code");
        const oidcState = urlHash.get("state");

        if (oidcCode && oidcState) {
          // Guard against StrictMode double-invocation FIRST before any other checks
          // The OIDC authorization code is single-use.
          const codeKey = `${oidcCode}:${oidcState}`;

          // Check if there's already a result from a previous effect invocation
          const existingResult = submissionResults.get(codeKey);
          if (existingResult) {
            setError(existingResult.error);
            setProcessing(existingResult.processing);
            return;
          }

          if (submittedCodes.has(codeKey)) {
            // Poll for result from the first submission
            const checkInterval = setInterval(() => {
              // Check if the first effect is redirecting
              if (isRedirecting.current) {
                clearInterval(checkInterval);
                return;
              }

              const result = submissionResults.get(codeKey);
              if (result) {
                clearInterval(checkInterval);
                setError(result.error);
                setProcessing(result.processing);
              }
            }, 100);

            // Cleanup interval after 30 seconds
            setTimeout(() => clearInterval(checkInterval), 30000);
            return;
          }
          submittedCodes.add(codeKey);

          // Check if we're already redirecting (avoid race condition on remount)
          if (isRedirecting.current) {
            return;
          }

          // Get code_verifier from localStorage (oidc-client-ts stores it with state as key)
          const storageKey = `oidc.${oidcState}`;
          const storedDataString = localStorage.getItem(storageKey);

          if (!storedDataString) {
            setError(
              "Authentication failed: Session expired. Please try logging in again.",
            );
            setProcessing(false);
            return;
          }

          let codeVerifier;
          try {
            const storedData = JSON.parse(storedDataString);
            codeVerifier = storedData.code_verifier;
          } catch (e) {
            setError("Authentication failed: Invalid session data");
            setProcessing(false);
            return;
          }

          if (!codeVerifier) {
            setError("Authentication failed: Missing code verifier");
            setProcessing(false);
            return;
          }

          // Exchange code via backend with CSRF protection
          const response = await fetch("/api/auth/oidc/token/", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRFToken": getCsrfToken() || "",
            },
            credentials: "include", // Include cookies for session and CSRF token
            body: JSON.stringify({
              code: oidcCode,
              code_verifier: codeVerifier,
              redirect_uri: `${window.location.origin}/auth/callback/`,
            }),
          });

          if (response.ok) {
            await response.json();

            // Mark that we're redirecting BEFORE any state updates
            isRedirecting.current = true;

            // Store success result so polling effects know to stop
            submissionResults.set(codeKey, { error: null, processing: false });

            // Clean up localStorage only after successful authentication
            localStorage.removeItem(storageKey);

            // Clear auth flow flag before redirect - auth is successful
            setAuthFlowActive(false);

            // Redirect to logged-in handler
            window.location.href = "/player/logged-in";
          } else {
            // Clean up localStorage on error
            localStorage.removeItem(storageKey);

            const errorData = await response.json().catch(() => ({}));

            // Handle registration required (403) - user authenticated but no profile
            if (
              response.status === 403 &&
              errorData.error === "registration_required"
            ) {
              // Use special marker for profile incomplete to show link
              const errorCode = "profile_incomplete";
              submissionResults.set(codeKey, {
                error: errorCode,
                processing: false,
              });

              setError(errorCode);
              setProcessing(false);
              return;
            }

            // Handle other authentication errors
            // Use error code for translation, fallback to message for English
            const errorCode = errorData.error;
            const fallbackMessage =
              errorData.message ||
              errorData.detail ||
              "Authentication failed. Please try again.";

            // Store both code and fallback message for translation
            submissionResults.set(codeKey, {
              error: errorCode
                ? `${errorCode}|${fallbackMessage}`
                : fallbackMessage,
              processing: false,
            });

            setError(
              errorCode ? `${errorCode}|${fallbackMessage}` : fallbackMessage,
            );
            setProcessing(false);
          }

          return;
        }

        // Check for error first
        const error = urlParams.get("error") || urlHash.get("error");
        const errorDescription =
          urlParams.get("error_description") ||
          urlHash.get("error_description");

        if (error) {
          setError(errorDescription || error);
          setProcessing(false);
          return;
        }

        // Get token from URL (could be in query params or hash fragment depending on flow)
        const idToken = urlParams.get("id_token") || urlHash.get("id_token");
        const code = urlParams.get("code");
        const state = urlParams.get("state") || urlHash.get("state");

        // Validate state parameter for CSRF protection - require exact match
        const savedState = sessionStorage.getItem("firebase_auth_state");

        if (state && savedState && state !== savedState) {
          // Only reject if BOTH states exist but don't match (actual CSRF attack)
          setError("Authentication failed: Invalid state parameter");
          sessionStorage.removeItem("firebase_auth_state");
          setProcessing(false);
          return;
        }

        if (!savedState && state) {
          // State was lost (sessionStorage cleared) - allow through
          // This may happen due to redirect to external auth provider
        }

        sessionStorage.removeItem("firebase_auth_state");

        if (!idToken && !code) {
          setError("Authentication failed: No token received");
          setProcessing(false);
          return;
        }

        // For authorization code flow, we'd need to exchange it for a token
        // For now, we only support ID token (implicit flow)
        if (!idToken && code) {
          setError(
            "Authentication flow not supported. Please contact support.",
          );
          setProcessing(false);
          return;
        }

        // Send token to backend for validation (existing Firebase endpoint)
        const response = await fetch("/api/auth/firebase/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          credentials: "include", // Important: include cookies for session
        });

        if (response.ok) {
          await response.json();

          // Clear any previous login errors
          localStorage.removeItem("firebase_loginError");

          // Set a flag so the app knows we're authenticated via Firebase
          sessionStorage.setItem("firebase_authenticated", "true");

          // Mark that we're redirecting using ref (synchronous update)
          isRedirecting.current = true;

          // Clear auth flow flag before redirect - auth is successful
          setAuthFlowActive(false);

          // Redirect to logged-in handler which routes based on entry status
          window.location.href = "/player/logged-in";
        } else {
          const errorData = await response.json().catch(() => ({}));

          // Handle registration required (403) - user authenticated but no profile
          if (
            response.status === 403 &&
            errorData.error === "registration_required"
          ) {
            setError("profile_incomplete");
            setProcessing(false);
            return;
          }

          // Handle other authentication errors
          // Use error code for translation, fallback to message for English
          const errorCode = errorData.error;
          const fallbackMessage =
            errorData.message ||
            errorData.detail ||
            "Authentication failed. Please try again.";

          setError(
            errorCode ? `${errorCode}|${fallbackMessage}` : fallbackMessage,
          );
          setProcessing(false);
        }
      } catch (err) {
        setError("Network error. Please try again.");
        setProcessing(false);
      }
    };

    processCallback();

    // Cleanup: Clear auth flow flag when component unmounts or redirect happens
    return () => {
      setAuthFlowActive(false);
    };
  }, []);

  // Don't render anything if we're redirecting (checked via ref for synchronous access)
  if (isRedirecting.current) {
    return (
      <Container>
        <Loading />
        <p>
          {t(
            "login.auth.completingAuthentication",
            "Completing authentication...",
          )}
        </p>
      </Container>
    );
  }

  if (processing) {
    return (
      <Container>
        <Loading />
        <p>
          {t(
            "login.auth.completingAuthentication",
            "Completing authentication...",
          )}
        </p>
      </Container>
    );
  }

  if (error && error.trim() && !isRedirecting.current) {
    // Parse error code and fallback message
    const [errorCode, fallbackMessage] = error.includes("|")
      ? error.split("|", 2)
      : [null, error];

    // Special handling for profile incomplete - show CTA link
    if (error === "profile_incomplete") {
      const lang = i18n.language || "ar"; // Default to Arabic
      const profileUrl = `https://www.spl.com.sa/${lang}/account/profile`;

      return (
        <Container>
          <Alert type="error">
            <strong>
              {t("login.auth.authenticationError", "Authentication Error")}
            </strong>
            <p>
              {t(
                "login.auth.errors.profile_incomplete_text",
                "We couldn't sign you in because your SPL profile isn't complete yet.",
              )}{" "}
              <a href={profileUrl}>
                {t(
                  "login.auth.errors.profile_incomplete_link",
                  "Click here to complete your profile",
                )}
              </a>
              .
            </p>
          </Alert>
          <a href="/">
            {t("login.auth.returnToHomepage", "Return to homepage")}
          </a>
        </Container>
      );
    }

    // Regular error handling
    const translationKey = errorCode ? `login.auth.errors.${errorCode}` : null;
    const displayMessage = translationKey
      ? t(translationKey, fallbackMessage)
      : fallbackMessage;

    return (
      <Container>
        <Alert type="error">
          <strong>
            {t("login.auth.authenticationError", "Authentication Error")}
          </strong>
          <p>{displayMessage}</p>
        </Alert>
        <a href="/">{t("login.auth.returnToHomepage", "Return to homepage")}</a>
      </Container>
    );
  }

  return null;
};
